import React, { useEffect, useRef, useState } from 'react';
import { NavLink, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Home,
  LayoutGrid,
  Sparkles,
  CalendarClock,
  LineChart,
  Settings,
  LogOut,
  Plus,
  ChevronDown,
  Check,
} from 'lucide-react';
import { panelPath } from '../config/paths';
import { useInstagramAccounts } from '../context/InstagramAccountContext';
import { useAuth } from '../context/AuthContext';

const MAIN_NAV: { to: string; labelKey: string; icon: typeof Sparkles; end?: boolean }[] = [
  { to: panelPath('/'), labelKey: 'nav.dashboard', icon: Home, end: true },
  { to: panelPath('/content'), labelKey: 'nav.content', icon: Sparkles },
  { to: panelPath('/schedule'), labelKey: 'nav.schedule', icon: CalendarClock },
  { to: panelPath('/metrics'), labelKey: 'nav.metrics', icon: LineChart },
  { to: panelPath('/reference-sources'), labelKey: 'nav.referenceSources', icon: LayoutGrid },
];

function accountLabel(a: { igUsername?: string; pageName?: string; id: string }) {
  return a.igUsername ? `@${a.igUsername}` : a.pageName || a.id.slice(0, 8);
}

function accountInitials(a: { igUsername?: string; pageName?: string }) {
  return (a.igUsername || a.pageName || '?').slice(0, 2).toUpperCase();
}

function AccountAvatar({
  account,
  sizeClass = 'h-6 w-6',
  textClass = 'text-[11px]',
}: {
  account: { igUsername?: string; pageName?: string; profilePictureUrl?: string };
  sizeClass?: string;
  textClass?: string;
}) {
  if (account.profilePictureUrl) {
    return (
      <img
        src={account.profilePictureUrl}
        alt={account.igUsername ? `@${account.igUsername}` : account.pageName || ''}
        referrerPolicy="no-referrer"
        className={`${sizeClass} shrink-0 rounded-full object-cover`}
      />
    );
  }
  return (
    <span
      className={`flex ${sizeClass} shrink-0 items-center justify-center rounded-full bg-[#e8ddd0] dark:bg-lc-borderStrong ${textClass} font-semibold text-[#241c14] dark:text-lc-text`}
    >
      {accountInitials(account)}
    </span>
  );
}

function navClass(isActive: boolean) {
  return `flex items-center gap-2.5 rounded-md px-3 py-2.5 text-sm transition-colors ${
    isActive
      ? 'bg-[#f0e6f0] dark:bg-lc-borderStrong text-lc-pink'
      : 'text-[#7a6b5d] dark:text-lc-faint hover:bg-[#f0e6f0]/60 dark:hover:bg-lc-borderStrong/50 hover:text-[#241c14] dark:hover:text-lc-soft'
  }`;
}

export default function Sidebar() {
  const { t } = useTranslation();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const { accounts, activeAccount, setActiveAccountId, loading } = useInstagramAccounts();
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(e: PointerEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const triggerLabel = loading
    ? t('common.loading')
    : activeAccount
      ? accountLabel(activeAccount)
      : t('accounts.noAccountsYet');

  return (
    <aside className="w-60 shrink-0 border-r border-[#e8ddd0] dark:border-lc-border bg-[#f5eff5] dark:bg-lc-surface backdrop-blur-sm flex flex-col gap-8 overflow-y-auto px-4 py-5">
      <Link to={panelPath('/')} className="flex items-center gap-2 px-1">
        <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-lc-pink text-sm font-bold text-lc-bg">
          L
        </span>
        <span className="text-base font-bold tracking-tight text-[#241c14] dark:text-lc-text">
          Loopcast
        </span>
      </Link>

      <nav className="flex flex-col gap-2 flex-1 min-h-0">
        <div className="px-3 mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#7a6b5d] dark:text-lc-faint">
          {t('nav.sectionAccounts')}
        </div>

        <div ref={dropdownRef} className="relative mb-3">
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-haspopup="listbox"
            className="flex w-full items-center gap-2.5 rounded-md border border-[#e8ddd0] dark:border-lc-border bg-white dark:bg-lc-bg px-3 py-2.5 text-left transition-colors hover:border-lc-pink/40"
          >
            {activeAccount ? (
              <AccountAvatar account={activeAccount} />
            ) : (
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#e8ddd0] dark:bg-lc-borderStrong text-[11px] font-semibold text-[#241c14] dark:text-lc-text">
                ?
              </span>
            )}
            <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-[#241c14] dark:text-lc-text">
              {triggerLabel}
            </span>
            <ChevronDown
              className={`h-4 w-4 shrink-0 text-[#7a6b5d] dark:text-lc-faint transition-transform ${open ? 'rotate-180' : ''}`}
              strokeWidth={2}
            />
          </button>

          {open && (
            <div
              role="listbox"
              className="absolute left-0 right-0 top-full z-30 mt-1.5 overflow-hidden rounded-md border border-[#e8ddd0] dark:border-lc-border bg-white dark:bg-lc-surface shadow-lg"
            >
              {loading ? (
                <p className="px-3 py-2.5 text-xs text-[#7a6b5d] dark:text-lc-faint">{t('common.loading')}</p>
              ) : accounts.length === 0 ? (
                <p className="px-3 py-2.5 text-xs text-[#7a6b5d] dark:text-lc-faint">{t('accounts.noAccountsYet')}</p>
              ) : (
                <div className="max-h-56 overflow-y-auto py-1">
                  {accounts.map((a) => {
                    const selected = activeAccount?.id === a.id;
                    return (
                      <button
                        key={a.id}
                        type="button"
                        role="option"
                        aria-selected={selected}
                        onClick={() => {
                          setActiveAccountId(a.id);
                          setOpen(false);
                          navigate(panelPath('/'));
                        }}
                        className={`flex w-full items-center gap-2.5 px-3 py-2 text-left text-[13px] transition-colors ${
                          selected
                            ? 'bg-[#f0e6f0] dark:bg-lc-borderStrong text-lc-pink'
                            : 'text-[#7a6b5d] dark:text-lc-faint hover:bg-[#f0e6f0]/60 dark:hover:bg-lc-borderStrong/50'
                        }`}
                      >
                        <AccountAvatar account={a} />
                        <span className="min-w-0 flex-1 truncate">{accountLabel(a)}</span>
                        {selected && <Check className="h-3.5 w-3.5 shrink-0" strokeWidth={2.5} />}
                      </button>
                    );
                  })}
                </div>
              )}
              <div className="border-t border-[#e8ddd0] dark:border-lc-border">
                <Link
                  to={panelPath('/accounts')}
                  onClick={() => setOpen(false)}
                  className="flex items-center gap-2.5 px-3 py-2.5 text-[13px] text-lc-purple hover:bg-[#f0e6f0]/60 dark:hover:bg-lc-borderStrong/50 transition-colors"
                >
                  <Plus className="h-4 w-4 shrink-0" strokeWidth={2} />
                  {t('nav.addAccount')}
                </Link>
              </div>
            </div>
          )}
        </div>

        <div className="px-3 mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#7a6b5d] dark:text-lc-faint">
          {t('nav.sectionMain')}
        </div>
        {MAIN_NAV.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) => navClass(isActive)}
          >
            <item.icon className="h-[18px] w-[18px] shrink-0" strokeWidth={2} />
            {t(item.labelKey)}
          </NavLink>
        ))}
      </nav>

      <div className="flex flex-col gap-2 pt-5 border-t border-[#e8ddd0] dark:border-lc-border mt-auto">
        <NavLink to={panelPath('/billing')} className={({ isActive }) => navClass(isActive)}>
          <Settings className="h-[18px] w-[18px] shrink-0" strokeWidth={2} />
          {t('nav.billing')}
        </NavLink>
        <button
          type="button"
          onClick={() => logout()}
          className="flex items-center gap-2.5 rounded-md px-3 py-2.5 text-[13px] text-[#7a6b5d] dark:text-lc-faint hover:bg-[#f0e6f0]/60 dark:hover:bg-lc-borderStrong/50 hover:text-[#241c14] dark:hover:text-lc-soft transition-colors w-full text-left"
        >
          <LogOut className="h-[18px] w-[18px] shrink-0" strokeWidth={2} />
          {t('common.logout')}
        </button>
      </div>
    </aside>
  );
}
